import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Navigation Views
 */
export const VIEWS = {
  CHAT: 'chat',
  TODOS: 'todos',
  PROGRESS_DASHBOARD: 'progress_dashboard',
  BRACELET: 'bracelet',
  ARTICLE_DETAIL: 'article_detail',
  QUIZ_DETAIL: 'quiz_detail',
  VIDEO_DETAIL: 'video_detail',
  NEWS_DETAIL: 'news_detail',
  ALL_ARTICLES: 'all_articles',
  ALL_NEWS: 'all_news',
  LEARNING_RESOURCES: 'learning_resources',
  CALENDLY: 'calendly',  // Legacy: external marketing-site booking iframe (nothing navigates here any more)
  COACHING_SCHEDULE: 'coaching_schedule',  // In-app booking: coach picker → slots (the header Coach slot lands here)
  COACHING_SESSION: 'coaching_session',  // Booked-session details: when, meeting link, coach info, reschedule/cancel
  COACH_DASHBOARD: 'coach_dashboard',
  ORG_DASHBOARD: 'org_dashboard',
  DEV_DASHBOARD: 'dev_dashboard',
  COMMUNITY_HUB: 'community_hub',  // Community Manager Hub (Community-org admins + dev-panel users)
  SETTINGS: 'settings',
  PRIVACY_POLICY: 'privacy_policy',
  TERMS_OF_SERVICE: 'terms_of_service',
  MONEY_DATES: 'money_dates',
  CIRCLES: 'circles',
  CIRCLE_SETTINGS: 'circle_settings',
  FAMILY: 'family'
};

/**
 * Custom hook for managing app navigation
 *
 * Provides:
 * - currentView: current active view
 * - viewData: data for the current view (e.g., article slug, news id)
 * - navigateTo: navigate to a new view with optional data
 * - goBack: return to previous view
 * - canGoBack: whether there's a previous view to go back to
 * - isTransitioning: whether a navigation transition is in progress
 */
export function useNavigation(initialView = VIEWS.CHAT) {
  const [currentView, setCurrentView] = useState(initialView);
  const [viewStack, setViewStack] = useState([initialView]);
  const [viewData, setViewData] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Live mirror of the stack for the (once-registered) popstate listener.
  const viewStackRef = useRef(viewStack);
  viewStackRef.current = viewStack;
  // Whether our sentinel history entry is currently armed (0 or 1 alive).
  const sentinelArmedRef = useRef(false);

  // App-level "modes" that are not views (e.g. circle chat) can join the
  // Back behaviour through this stable object:
  //  - backGuard.rootPop: called when Back is pressed with no views to pop;
  //    return true if the mode consumed the press (e.g. exited the circle).
  //  - backGuard.rootPending: keep true while such a mode is active, so the
  //    guard re-arms after view pops instead of letting the next press exit.
  //  - backGuard.arm(): arm the sentinel when the mode is entered.
  const backGuardRef = useRef({
    rootPop: null,
    rootPending: false,
    arm: () => {
      if (!sentinelArmedRef.current) {
        window.history.pushState({ sophiaViewGuard: true }, '');
        sentinelArmedRef.current = true;
      }
    }
  });

  /**
   * Navigate to a new view
   * @param {string} view - View to navigate to (from VIEWS constant)
   * @param {object} data - Optional data for the view (e.g., { slug: 'article-slug' })
   */
  const navigateTo = useCallback((view, data = {}) => {
    setIsTransitioning(true);

    // Views are React state, not URLs — without a history entry the phone's
    // Back button/gesture would leave the site instead of going to the
    // previous screen. Arm ONE sentinel entry when leaving home (same
    // pattern OnboardingScreen uses); the popstate handler below re-arms it
    // per press while the stack is deep.
    if (!sentinelArmedRef.current) {
      window.history.pushState({ sophiaViewGuard: true }, '');
      sentinelArmedRef.current = true;
    }

    // Add to view stack
    setViewStack(prev => [...prev, view]);
    setCurrentView(view);
    setViewData(data);

    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 300);
  }, []);

  /**
   * Replace the current view instead of stacking on top of it — for flows
   * where the finished step must not be revisitable via Back (e.g. booking →
   * session details, or reschedule → the coach's slots). From home it simply
   * navigates (home always stays at the bottom of the stack).
   */
  const replaceTo = useCallback((view, data = {}) => {
    setIsTransitioning(true);

    if (!sentinelArmedRef.current) {
      window.history.pushState({ sophiaViewGuard: true }, '');
      sentinelArmedRef.current = true;
    }

    setViewStack(prev => (prev.length > 1 ? [...prev.slice(0, -1), view] : [...prev, view]));
    setCurrentView(view);
    setViewData(data);

    setTimeout(() => setIsTransitioning(false), 300);
  }, []);

  /**
   * Go back to the previous view
   */
  const goBack = useCallback(() => {
    if (viewStack.length <= 1) return;

    setIsTransitioning(true);

    setViewStack(prev => {
      const newStack = [...prev];
      newStack.pop(); // Remove current view
      const previousView = newStack[newStack.length - 1];
      setCurrentView(previousView);

      // Clear view data when going back
      setViewData({});

      return newStack;
    });

    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 300);
  }, [viewStack]);

  /**
   * Navigate to home/chat view and clear stack
   */
  const goHome = useCallback(() => {
    setIsTransitioning(true);
    setCurrentView(VIEWS.CHAT);
    setViewStack([VIEWS.CHAT]);
    setViewData({});
    setTimeout(() => setIsTransitioning(false), 300);
  }, []);

  /**
   * Check if we can go back
   */
  const canGoBack = viewStack.length > 1;

  // Hardware/browser Back: navigate the app's screens, never out of the page.
  // Each press consumes the sentinel entry; while there are screens to pop we
  // go back one and immediately re-arm, so the NEXT press is intercepted too.
  // Once we reach home the sentinel is not re-armed and Back behaves normally
  // (exits the app) — per product direction.
  const goBackRef = useRef(null);
  useEffect(() => {
    goBackRef.current = goBack;
  }, [goBack]);

  useEffect(() => {
    const handlePopState = () => {
      sentinelArmedRef.current = false; // the press consumed our entry
      const guard = backGuardRef.current;
      if (viewStackRef.current.length > 1) {
        goBackRef.current();
        // Re-arm if this pop does NOT land us on home, or if an app-level
        // mode (circle chat) is still active underneath — the next press
        // must be interceptable to exit that mode rather than the page.
        if (viewStackRef.current.length > 2 || guard.rootPending) {
          window.history.pushState({ sophiaViewGuard: true }, '');
          sentinelArmedRef.current = true;
        }
        return;
      }
      // No views to pop: give the active app-level mode the press. Landing
      // back on plain home leaves the guard unarmed, so the NEXT press
      // exits normally.
      if (guard.rootPop && guard.rootPop()) {
        // consumed (mode exited) — nothing to re-arm
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return {
    currentView,
    viewData,
    navigateTo,
    replaceTo,
    goBack,
    goHome,
    canGoBack,
    isTransitioning,
    viewStack,
    backGuard: backGuardRef.current
  };
}
